import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { X, Trash2, Check, ListChecks } from "lucide-react";
import ProductAddToCart from "../routs/productPage/ProductAddToCart";
import PopUp from "../containers/PopUp";
import CartShoppingListPicker from "../routs/cart/CartShoppingListPicker";
import { upsertCartItem, clearCartAll } from "../../server/CollectSite";
import { setToggleCart } from "../../server/CartsOpen";
import { setFilterSort } from "../../server/Product";
import { newGlobalFileServer } from "../../../axiosInstance";
import {
  isCartDrawerRoute,
  isRetailCustomerLoggedIn,
} from "../../utils/cartDrawerRoutes";

/** True once window ≥ 1024px; re-evaluates on resize. */
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

function cartLineImageSrc(product) {
  const mediaArray = Array.isArray(product?.media) ? product.media : [];
  const path =
    mediaArray.length > 0 ? mediaArray[mediaArray.length - 1]?.path : null;
  return path
    ? `${newGlobalFileServer}${path}?width=80&height=80`
    : `${newGlobalFileServer}placeholder.jpg`;
}

function CartLineMeta({ product }) {
  if (!product) return null;
  const kashrut = product.kosherSupervision?.[0]?.classification ?? "";

  return (
    <div className="prod-info-line">
      {product.subBrand?.name ? (
        <>
          <p className="prod-brand">{product.subBrand.name}</p>
          <span className="line">|</span>
        </>
      ) : null}

      {product.weightDetails ? (
        <>
          {product.logisticsDetails?.packageQuantity > 1 ? (
            <span className="prod-details">
              {product.logisticsDetails.packageQuantity} ×
            </span>
          ) : null}
          <span className="prod-details">
            {product.weightDetails?.netContent}
          </span>
          <span className="prod-details">
            {product.weightDetails.unitOfMeasure}
          </span>
        </>
      ) : null}

      {(kashrut === "בשרי" || kashrut === "חלבי" || kashrut === "פרווה") && (
        <>
          {(product.weightDetails || product.subBrand?.name) && (
            <span className="line">|</span>
          )}
          <span
            className={
              kashrut === "בשרי"
                ? "fleshy"
                : kashrut === "חלבי"
                  ? "milky"
                  : "parve"
            }
          >
            {kashrut}
          </span>
        </>
      )}
    </div>
  );
}

/** Fixed header children do not expand `<header>` layout — measure shells directly. */
function measureRetailHeaderBottomPx() {
  const cssBottom = parseFloat(
    getComputedStyle(document.documentElement)
      .getPropertyValue("--shell-fixed-stack-bottom")
      .trim(),
  );
  if (Number.isFinite(cssBottom) && cssBottom > 0) {
    return cssBottom;
  }
  const wrap = document.querySelector(".header-wrapper");
  const row = document.querySelector(".header-second-row");
  const bottoms = [];
  if (wrap) bottoms.push(wrap.getBoundingClientRect().bottom);
  if (row) bottoms.push(row.getBoundingClientRect().bottom);
  if (!bottoms.length) {
    const header = document.querySelector("header");
    if (header) bottoms.push(header.getBoundingClientRect().bottom);
  }
  return bottoms.length ? Math.max(...bottoms) : 0;
}

const CartDrawer = ({ setProductPopUp, setSelectedProd }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isDesktop = useIsDesktop();
  const [clearConfirm, setClearConfirm] = useState(false);
  const [showListPicker, setShowListPicker] = useState(false);
  const clearConfirmTimer = useRef(null);

  const openCart = useSelector((state) => state.openCart.openCart);
  const { role, user, authDialogOpen } = useSelector((state) => ({
    role: state.user?.role,
    user: state.user?.user,
    authDialogOpen: Boolean(state.user?.userEntry?.[0]),
  }));
  const canShowCart = isRetailCustomerLoggedIn(user, role) && !authDialogOpen;
  const cartOpen = Boolean(openCart && canShowCart);
  const isCategoryRoute = isCartDrawerRoute(pathname);
  /* Cart is visible on home + category/search/sales after login. On desktop it opens
     automatically; on mobile it follows the toggle. Hidden for guests and auth dialog. */
  const drawerOpen = canShowCart && isCategoryRoute && (isDesktop || cartOpen);

  const { productsInCart, orderSum, stationId } = useSelector((state) => ({
    productsInCart: state.productsInCart?.productsInCart ?? [],
    orderSum: state.productsInCart?.orderSum,
    stationId: state.user?.stationId,
  }));

  const subtotal = useMemo(
    () => productsInCart.reduce((t, item) => t + item.totalPrice, 0),
    [productsInCart],
  );

  const saleTotal = useMemo(
    () =>
      productsInCart.reduce((total, item) => {
        if (item.promoId) {
          return total + (item.unitPrice * item.quantity - item.totalPrice);
        }
        return total;
      }, 0),
    [productsInCart],
  );

  const totalPay = parseFloat(orderSum ?? subtotal);

  const itemQty = useMemo(
    () => productsInCart.reduce((n, p) => n + (Number(p.quantity) || 0), 0),
    [productsInCart],
  );

  const close = () => {
    dispatch(setToggleCart(false));
    dispatch(setFilterSort(false));
  };

  /* body class only needed on mobile (desktop cart is always open) */
  useEffect(() => {
    if (!isDesktop && cartOpen) {
      document.body.classList.add("cart-drawer-open");
    } else {
      document.body.classList.remove("cart-drawer-open");
    }
    return () => document.body.classList.remove("cart-drawer-open");
  }, [isDesktop, cartOpen]);

  /** Keep drawer below fixed header shells (wrapper + second row with cart strip + buttons). */
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const root = document.documentElement;
    const wrap = document.querySelector(".header-wrapper");
    const row = document.querySelector(".header-second-row");
    const observed = [wrap, row].filter(Boolean);

    if (!observed.length) {
      return undefined;
    }

    const syncTop = () => {
      const bottom = Math.ceil(measureRetailHeaderBottomPx());
      root.style.setProperty(
        "--cart-drawer-top-offset",
        `${Math.max(0, bottom + 2)}px`,
      );
    };

    syncTop();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(syncTop);
    });
    observed.forEach((el) => ro.observe(el));
    window.addEventListener("resize", syncTop);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncTop);
      root.style.removeProperty("--cart-drawer-top-offset");
    };
  }, []);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const id = requestAnimationFrame(() => {
      const bottom = Math.ceil(measureRetailHeaderBottomPx());
      document.documentElement.style.setProperty(
        "--cart-drawer-top-offset",
        `${Math.max(0, bottom + 2)}px`,
      );
    });
    return () => cancelAnimationFrame(id);
  }, [drawerOpen]);

  useEffect(() => {
    if (!canShowCart) {
      dispatch(setToggleCart(false));
      dispatch(setFilterSort(false));
    }
  }, [canShowCart, dispatch]);

  useEffect(() => {
    // On mobile only: close cart when navigating away from filter routes.
    if (!isDesktop && !isCartDrawerRoute(pathname)) {
      dispatch(setToggleCart(false));
      dispatch(setFilterSort(false));
    }
  }, [pathname, dispatch, isDesktop]);

  const handleDelete = (element) => {
    dispatch(
      upsertCartItem(
        element.productId,
        parseFloat(element.unitPrice).toFixed(2),
        0,
        String(stationId),
        element.product.pricing?.promotions?.length
          ? element.product.pricing.promotions[0].promoId
          : undefined,
        element.product,
      ),
    );
  };

  const handleClearCart = () => {
    if (!clearConfirm) {
      setClearConfirm(true);
      clearConfirmTimer.current = setTimeout(
        () => setClearConfirm(false),
        3000,
      );
    } else {
      clearTimeout(clearConfirmTimer.current);
      setClearConfirm(false);
      dispatch(clearCartAll(stationId));
    }
  };

  const isInSale = (element) =>
    element.promoId &&
    element.quantity * element.unitPrice !== element.totalPrice;

  return (
    <>
      <div
        className={`cart-drawer-root${drawerOpen ? " cart-drawer-root--open" : ""}`}
        aria-hidden={!isDesktop && !cartOpen}
      >
        <aside
          className={`cart-drawer${isDesktop && drawerOpen ? " cart-drawer--desktop-permanent" : ""}${cartOpen ? " cart-drawer--open" : ""}`}
          aria-label="עגלת הקניות"
          dir="rtl"
        >
          <header className="cart-drawer__head">
            <div className="cart-drawer__head-text">
              <h2 className="cart-drawer__head-title">עגלת הקניות</h2>
              {itemQty > 0 ? (
                <p className="cart-drawer__head-sub">{itemQty} פריטים</p>
              ) : null}
            </div>
            {productsInCart.length > 0 && (
              <button
                type="button"
                className={`cart-drawer__clear-btn${clearConfirm ? " cart-drawer__clear-btn--confirm" : ""}`}
                onClick={handleClearCart}
                aria-label={
                  clearConfirm ? "לחץ לאישור ריקון העגלה" : "רוקן עגלה"
                }
              >
                {clearConfirm ? (
                  <>
                    <Check size={13} strokeWidth={2.5} aria-hidden />
                    <span>אישור?</span>
                  </>
                ) : (
                  <Trash2 size={15} strokeWidth={2} aria-hidden />
                )}
              </button>
            )}
            {/* Close button only on mobile */}
            {!isDesktop && (
              <button
                type="button"
                className="cart-drawer__head-close"
                onClick={close}
                aria-label="סגור"
              >
                <X size={20} strokeWidth={2} aria-hidden />
              </button>
            )}
          </header>

          <>
            <div className="cart-drawer__body">
              <div className="cart-drawer__list">
                {productsInCart.length === 0 ? (
                  <div className="co-empty-cart">
                    <p className="co-empty-cart__message">העגלה ריקה 🛒</p>
                    <button
                      type="button"
                      className="co-empty-cart__btn"
                      onClick={() => setShowListPicker(true)}
                    >
                      <ListChecks size={18} strokeWidth={2} aria-hidden />
                      בחר מרשימת קניות
                    </button>
                  </div>
                ) : (
                  productsInCart.map((element) => {
                    const inSale = isInSale(element);
                    const img = cartLineImageSrc(element.product);
                    const openProduct = () => {
                      setProductPopUp(true);
                      setSelectedProd(element.product);
                    };
                    const savedStr = inSale
                      ? (
                          element.unitPrice * element.quantity -
                          element.totalPrice
                        ).toFixed(2)
                      : null;
                    const dealNote = [
                      element.promoId ? "במבצע" : null,
                      inSale && savedStr ? `חסכת ${savedStr} ₪` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <article
                        key={element.productId}
                        className="cart-drawer-line cart-drawer-line--narrow"
                      >
                        <div className="cart-drawer-line__layout">
                          <button
                            type="button"
                            className="cart-drawer-line__thumb-btn"
                            onClick={openProduct}
                            aria-label={`פרטי מוצר: ${element.product?.name ?? ""}`}
                          >
                            <span className="cart-drawer-line__thumb-wrap">
                              <img
                                loading="lazy"
                                src={img}
                                alt=""
                                className="cart-drawer-line__thumb-img"
                              />
                            </span>
                          </button>
                          <div className="cart-drawer-line__content">
                            <div className="cart-drawer-line__top">
                              <button
                                type="button"
                                className="cart-drawer-line__title-block"
                                onClick={openProduct}
                              >
                                <span className="cart-drawer-line__name">
                                  {element.product?.name}
                                </span>
                              </button>
                              <button
                                type="button"
                                className="cart-drawer-line__dismiss-header"
                                aria-label="הסר מהעגלה"
                                onClick={() => handleDelete(element)}
                              >
                                <X size={14} strokeWidth={2} aria-hidden />
                              </button>
                            </div>
                            <div className="cart-drawer-line__bottom">
                              <button
                                type="button"
                                className="cart-drawer-line__details-hit"
                                onClick={openProduct}
                              >
                                <div className="cart-drawer-line__details-zone">
                                  {dealNote ? (
                                    <div className="cart-drawer-line__deal-note">
                                      {dealNote}
                                    </div>
                                  ) : null}
                                  <div className="cart-drawer-line__details-lines">
                                    <CartLineMeta product={element.product} />
                                    {element.product?.description ? (
                                      <span className="cart-drawer-line__desc">
                                        {element.product.description}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </button>
                              <div
                                className="cart-drawer-line__qty-zone"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                <div className="cart-drawer-line__qty">
                                  <ProductAddToCart
                                    element={element}
                                    haveInfo={false}
                                    retailCard
                                  />
                                </div>
                              </div>
                              <button
                                type="button"
                                className="cart-drawer-line__price-hit"
                                onClick={openProduct}
                              >
                                <span className="cart-drawer-line__price-block">
                                  <span className="cart-drawer-line__price-num">
                                    {element.totalPrice.toFixed(2)}
                                  </span>
                                  <span className="cart-drawer-line__price-sym">
                                    {" "}
                                    ₪
                                  </span>
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>

              <div className="cart-drawer__summary">
                <NavLink
                  to="/cart"
                  className="cart-drawer__cta cart-drawer__summary-cta"
                  onClick={close}
                >
                  <span className="cart-drawer__cta-pay">
                    <span className="cart-drawer__cta-pay-total">
                      {totalPay.toFixed(2)} ₪
                    </span>
                    {saleTotal > 0 ? (
                      <span className="cart-drawer__cta-pay-saved">
                        חסכת {saleTotal.toFixed(2)} ₪
                      </span>
                    ) : null}
                  </span>
                  <span className="cart-drawer__cta-label">המשך לתשלום</span>
                </NavLink>
              </div>
            </div>
          </>
        </aside>
      </div>

      {showListPicker ? (
        <PopUp
          width="480px"
          close={() => setShowListPicker(false)}
          wrapperClassName="popup-wrapper--shopping-list"
        >
          <CartShoppingListPicker close={() => setShowListPicker(false)} />
        </PopUp>
      ) : null}
    </>
  );
};

export default CartDrawer;
